import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ScrollArea } from "~/components/ui/scroll-area";
import { deserializeColor } from "~/features/settings/components/categories/ColorPicker";

export interface CategoryOption {
  id: number;
  name: string;
  color?: string;
  folderName?: string;
}

interface CategorySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: CategoryOption[];
  initialExcludedNames?: string[];
  onConfirm: (excludedNames: string[]) => void;
}

export function CategorySelectionModal({
  isOpen,
  onClose,
  categories,
  initialExcludedNames = [],
  onConfirm,
}: CategorySelectionModalProps) {
  const [excludedNames, setExcludedNames] = useState<Set<string>>(
    new Set(initialExcludedNames)
  );

  const handleToggleCategory = (categoryName: string) => {
    setExcludedNames((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        if (categories.length - prev.size <= 1) return prev;
        next.add(categoryName);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setExcludedNames(new Set());
  };

  const handleDeselectAll = () => {
    const allNames = categories.map((c) => c.name);
    const firstSelected = categories.find((c) => !excludedNames.has(c.name));
    const keepName = firstSelected?.name || allNames[0];
    setExcludedNames(new Set(allNames.filter((n) => n !== keepName)));
  };

  const handleConfirm = () => {
    onConfirm(Array.from(excludedNames));
    onClose();
  };

  const selectedCount = categories.length - excludedNames.size;
  const allSelected = excludedNames.size === 0;
  const noneSelected = excludedNames.size === categories.length;
  const someSelected = !allSelected && !noneSelected;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="template.selectCategories"
              defaultMessage="Select Categories for Document List"
            />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="select-all"
                checked={allSelected}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleSelectAll();
                  } else {
                    handleDeselectAll();
                  }
                }}
                className={
                  someSelected ? "data-[state=checked]:bg-primary/50" : ""
                }
              />
              <label
                htmlFor="select-all"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {allSelected ? (
                  <FormattedMessage
                    id="template.selectAll"
                    defaultMessage="All Categories"
                  />
                ) : noneSelected ? (
                  <FormattedMessage
                    id="template.noneSelected"
                    defaultMessage="None Selected"
                  />
                ) : (
                  <FormattedMessage
                    id="template.someSelected"
                    defaultMessage="{count} of {total} Selected"
                    values={{
                      count: categories.length - excludedNames.size,
                      total: categories.length,
                    }}
                  />
                )}
              </label>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                disabled={allSelected}
              >
                <FormattedMessage
                  id="template.selectAll"
                  defaultMessage="Select All"
                />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeselectAll}
                disabled={selectedCount <= 1}
              >
                <FormattedMessage
                  id="template.deselectAll"
                  defaultMessage="Deselect All"
                />
              </Button>
            </div>
          </div>

          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {categories.map((category) => {
                const isExcluded = excludedNames.has(category.name);
                return (
                  <div
                    key={category.id}
                    className="flex items-center space-x-3 rounded-md p-2 hover:bg-accent"
                  >
                    <Checkbox
                      id={`category-${category.id}`}
                      checked={!isExcluded}
                      disabled={!isExcluded && selectedCount === 1}
                      onCheckedChange={() =>
                        handleToggleCategory(category.name)
                      }
                    />
                    <label
                      htmlFor={`category-${category.id}`}
                      className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        {category.color && (
                          <div
                            className="w-3 h-3 rounded-sm"
                            style={{
                              backgroundColor: deserializeColor(
                                category.color || ""
                              ).hex,
                            }}
                          />
                        )}
                        <span>{category.name}</span>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleConfirm}>
            <FormattedMessage id="confirm" defaultMessage="Confirm" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
